import { useBillingCurrency } from "$global-providers/billing-provider/provider";
import useCurrencyFormat from "$routes/_lib/use-currency-format";
import { useCallback, useMemo } from "react";
import type { FormatInvoiceMoney } from "./invoice-money";

/**
 * The card prices in the currency the invoice itself was raised in, laid out
 * with the facility's local conventions: the currency code decides the symbol,
 * the territory's locale decides grouping and where that symbol sits.
 */
export const useInvoiceMoneyFormat = (
  invoiceCurrency: string | null | undefined,
): FormatInvoiceMoney => {
  const billingCurrency = useBillingCurrency();

  const currency = useMemo(
    () =>
      invoiceCurrency
        ? { ...billingCurrency, code: invoiceCurrency }
        : billingCurrency,
    [billingCurrency, invoiceCurrency],
  );

  const formatter = useCurrencyFormat(currency);

  return useCallback(
    (amountInMajorUnits: number) =>
      // A facility with no territory configured has no locale to format
      // against; the figures still have to render, so they fall back to bare
      // grouped decimals rather than vanishing.
      formatter
        ? formatter.format(amountInMajorUnits)
        : amountInMajorUnits.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
    [formatter],
  );
};
